import { SOCIAL_AD_TYPES } from "utils/constants";
import {
	PAC_FORM_TYPES, FACEBOOK_DISCLAIMER_DEFAULT_ID, TIKTOK_DISCLAIMER_DEFAULT_TYPE,
	IMPOSIUM_PROJECT_CREATION_METHOD, IMPOSIUM_COMP_CREATION_METHOD
} from "social/constants/common";
import { getNewClientID } from "utils/common";
import { tellSentry } from "utils/Rave";
import { createClickableOption } from "social/TikTokDisclaimer";
import { PINTEREST_NO_DISCLOSURE } from "social/validators/pinterest";


function _getCreativeLevelValues(value){
	let {
		name = "",
		isDynamic,
		assetType,
	} = value;

	return {
		name,
		isDynamic,
		assetType,
	};
}

export function defaultDeserializer(values, adType) {
	const {
		socialValues = {},
	} = values;

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			...deserializeAfterEffectsComposition(values),
		},
	};
}

export function deserializeFacebook(values, adType) {
	const {
		socialValues = {},
	} = values;

	const placements = socialValues.placements || [];
	const deserializedPlacements = placements.reduce((prev, placement) => {
		const { name } = placement.type;
		prev[name] = { ...placement };
		return prev;
	}, {});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta: socialValues.cta?.name,
			placements: deserializedPlacements,
			_currentActivePAC: PAC_FORM_TYPES.DEFAULT, // This field does not get sent to the API, but it is used for tracking the active placement within an adType
		},
	};
}

export const deserializeSnapchat = (values, adType) => {
	let {
		socialValues = {},
	} = values;
	const { disclaimerText, disclaimerName } = socialValues;
	socialValues.disclaimerEnabled = !!disclaimerText?.value || !!disclaimerName?.value;

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...deserializeImposiumData(values),
			...socialValues,
			...deserializeAfterEffectsComposition(values),
		},
	};
};

export const deserializePinterest = (values, adType) => {
	let {
		socialValues = {},
	} = values;
	const { disclosureType, disclosureUrl } = socialValues;
	socialValues.disclosureEnabled = !!disclosureType?.value && disclosureType.value !== PINTEREST_NO_DISCLOSURE || !!disclosureUrl?.value;
	socialValues.disclosureType = disclosureType?.value || PINTEREST_NO_DISCLOSURE;
	socialValues.disclosureUrl = disclosureUrl?.value;

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			...deserializeImposiumData(values),
			...deserializeAfterEffectsComposition(values),
		},
	};
};

const deserializeFacebookDisclaimer = (socialValues) => {
	const { disclaimer, ...restSocialValues } = socialValues;

	return {
		...restSocialValues,
		disclaimerEnabled: !!disclaimer,
		disclaimerText: disclaimer?.text || null,
		disclaimerUrl: disclaimer?.url || null,
		disclaimerId: disclaimer?.id || FACEBOOK_DISCLAIMER_DEFAULT_ID,
	};
};

export function deserializeImposiumComposition(values){
	return  {
		imposiumComposition: {
			id: values.compositionId,
			creationMethod: IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION,
		},
	};
}

export function deserializeImposiumCard(card, values){
	const result =  {
		imposiumComposition: {
			id: card.compositionId,
			creationMethod: IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION,
		},
	};
	if (card.order === 0){
		result.imposiumProject = {
			id: values.storyId,
			creationMethod: IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT,
		};
	}
	return result;
}

export function deserializeImposiumProject(values){
	return  {
		imposiumProject: {
			id: values.storyId,
			creationMethod: IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT,
		},
	};
}

export function deserializeImposiumData(values){
	if (values.storyId) {
		return  {
			imposiumProject: {
				id: values.storyId,
				creationMethod: IMPOSIUM_PROJECT_CREATION_METHOD.EXISTING_PROJECT,
			},
			imposiumComposition: {
				id: values.compositionId,
				creationMethod: IMPOSIUM_COMP_CREATION_METHOD.EXISTING_COMPOSITION,
			},
		};
	}
	return {};
}

export function deserializeAfterEffectsComposition(values) {
	const result = {};
	if (values.aeCompositionId) {
		result.afterEffectsComposition = {
			id: values.aeCompositionId,
		};
	}
	if (values.frameNumber !== null && values.frameNumber !== undefined) {
		result.frameNumber = values.frameNumber;
	}
	return result;
}

export function deserializeFacebookMO(values, adType){
	let {
		socialValues = {},
	} = values;
	socialValues = deserializeFacebookDisclaimer(socialValues);

	const placements = socialValues.placements || [];
	const deserializedPlacements = placements.reduce((prev, placement) => {
		const { name } = placement.type;
		prev[name] = {
			...placement,
			...deserializeImposiumComposition(placement),
			...deserializeAfterEffectsComposition(placement),
		};
		return prev;
	}, {});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			...deserializeImposiumData(values),
			...deserializeAfterEffectsComposition(values),
			cta:{
				...socialValues.cta,
				value:socialValues.cta?.name,
			},
			// cta: socialValues.cta?.name,
			placements: deserializedPlacements,
			_currentActivePAC: PAC_FORM_TYPES.DEFAULT, // This field does not get sent to the API, but it is used for tracking the active placement within an adType
		},
	};
}

export function deserializeFacebookCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta: socialValues.cta?.name,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeFacebookCarouselMO(values, adType) {
	let {
		socialValues = {},
	} = values;
	socialValues = deserializeFacebookDisclaimer(socialValues);

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			...deserializeImposiumCard(card, values),
			clientId: getNewClientID(),
			...deserializeAfterEffectsComposition(card),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			cta:{
				...socialValues.cta,
				value:socialValues.cta?.name,
			},
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeGoogleDemandGenCarousel(values, adType, socialState) {
	const {
		socialValues = {},
		name = "",
	} = values;

	socialValues.requiredAspectRatios = (socialValues.aspectRatios || []).reduce((prev, ratio) => {
		prev[ratio] = true;
		return prev;
	}, {});

	delete socialValues.aspectRatios;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		const locale = card.cta?.locale || "en";
		const languageName = socialState?.googleLocales?.locales?.[locale]?.name || "English";
		const language = {
			id: locale,
			name: languageName,
			value: locale,
			label: languageName,
		};

		const formattedImages = (card.images || []).reduce((prev, image) => {
			const { aspectRatio, ...imageData } = image || {};
			try{
				prev[aspectRatio] = imageData;
			} catch {
				tellSentry("Google Demand Gen Carousel image has unknown or missing aspectRatio", aspectRatio);
			}

			return prev;
		}, {});

		return {
			...card,
			images: formattedImages,
			cta: card.cta?.name,
			language,
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			name,
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializePinterestCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			...deserializeImposiumCard(card, values),
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeLinkedInCarousel(values, adType) {
	const {
		socialValues = {},
	} = values;

	const serializedCards = socialValues.cards?.length ? socialValues.cards : [
		{
			type: "image",
			order: 0,
		},
		{
			type: "image",
			order: 1,
		}
	];

	const cards = (serializedCards).map((card) => {
		return {
			...card,
			...deserializeImposiumCard(card, values),
			clientId: getNewClientID(),
		};
	});

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...socialValues,
			currentCard: 0,
			cards,
		},
	};
}

export function deserializeGoogleDemandGenImage(values, adType, socialState) {
	const {
		socialValues = {},
		name = "",
	} = values;

	function deserializeTextFieldArray(field) {
		if(field.length) {
			return field.map(val => val.name);
		}
		return [null];
	}

	const { headlines, descriptions, cta } = socialValues || {};
	const deseralizedHeadlines = deserializeTextFieldArray(headlines);
	const deserializedDescriptions = deserializeTextFieldArray(descriptions);

	const locale = cta?.locale || "en";
	const languageName = socialState?.googleLocales?.locales?.[locale]?.name || "English";
	const language = {
		id: locale,
		name: languageName,
		value: locale,
		label: languageName,
	};

	return {
		adType,
		[adType]: {
			name,
			...socialValues,
			language,
			cta: cta?.name,
			headlines: deseralizedHeadlines,
			descriptions: deserializedDescriptions,
		},
	};
}

export function deserializeSnapchatCarousel(values, adType) {
	const {
		name = "",
		socialValues = {},
	} = values;
	const { disclaimerText, disclaimerName } = socialValues;
	socialValues.disclaimerEnabled = !!disclaimerText?.value || !!disclaimerName?.value;

	const { discovery, ...restValues } = socialValues;

	// TODO hopi-3176 cards
	return {
		adType,
		[adType]: {
			name,
			...restValues,
			...deserializeImposiumData(values),
			discoveryHeadline: discovery?.headline || {},
			discoveryBackgroundImage: discovery?.backgroundImage || {},
			discoveryLogoImage: discovery?.logoImage || {},
		},
	};
}

const deserializeTikTokDisclaimer = (socialValues) => {
	const { disclaimer, ...restSocialValues } = socialValues;
	const hasDisclaimer = !!disclaimer;

	return {
		...restSocialValues,
		disclaimerEnabled: hasDisclaimer,
		disclaimerType: disclaimer?.disclaimerType || TIKTOK_DISCLAIMER_DEFAULT_TYPE,
		disclaimerText: disclaimer?.disclaimerText || null,
		clickableOptions: (disclaimer?.clickableOptions || [{}]).map(createClickableOption),
	};
};

const TIKTOK_APPROVED_BANNER_TEXT = "This creative is approved. Only disclaimer changes are allowed.";

export function deserializeTikTokVideo(values, adType) {
	const result = defaultDeserializer(values, adType);
	result[adType] = {
		...deserializeTikTokDisclaimer(result[adType] || {}),
		...deserializeImposiumData(values),
	};

	result.infoBannerContent = TIKTOK_APPROVED_BANNER_TEXT;
	result.canSave = true; // TikTok Videos can have disclaimers added/edited even after they've been approved.
	return {
		...result,
	};
}

export const deserializeReddit = (values, adType) => {
	let {
		socialValues = {},
	} = values;
	const { disclaimerText } = socialValues;
	socialValues.disclaimerEnabled = !!disclaimerText?.value;

	return {
		adType,
		[adType]: {
			..._getCreativeLevelValues(values),
			...deserializeImposiumData(values),
			...socialValues,
			...deserializeAfterEffectsComposition(values),
		},
	};
};

const DESERIALIZERS_BY_ADTYPE = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: deserializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: deserializeFacebook,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: deserializeFacebookCarousel,
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: deserializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: deserializeFacebookMO,
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: deserializeFacebookCarouselMO,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: deserializeGoogleDemandGenCarousel,
	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: deserializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: deserializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: deserializePinterest,
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: deserializePinterestCarousel,
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: deserializeLinkedInCarousel,
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: deserializeGoogleDemandGenImage,
	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: deserializeSnapchat,
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: deserializeSnapchat,
	[SOCIAL_AD_TYPES.SNAPCHAT_CAROUSEL]: deserializeSnapchatCarousel,
	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: deserializeTikTokVideo,
	[SOCIAL_AD_TYPES.REDDIT_IMAGE]: deserializeReddit,
	[SOCIAL_AD_TYPES.REDDIT_VIDEO]: deserializeReddit,
};

export function deserializeByAdType(values, adType, socialState = {}) {
	const deserializer = DESERIALIZERS_BY_ADTYPE[adType] || defaultDeserializer;
	return deserializer(values, adType, socialState);
}
